import * as THREE from "three";

import type { LoomControlSurfaceAcceptedGeometry } from "../../api/loomControlSurfaceRebuild";
import type { LoomPoint3 } from "./loomControlSurfaceGeometry";
import type {
  LoomSha256Revision,
  LoomWingControlSurfaceCollection,
  LoomWingControlSurfacePose,
} from "./loomWingControlSurfaceContract";

interface SyncInput {
  collection: LoomWingControlSurfaceCollection;
  geometry: LoomControlSurfaceAcceptedGeometry;
  pose: LoomWingControlSurfacePose;
  poseRevision: LoomSha256Revision;
  selectedBodyId: string | null;
}

export interface LoomControlSurfaceSceneReceipt {
  readonly appliedAnglesDeg: Readonly<Record<string, { readonly port: number; readonly starboard: number }>>;
  readonly appliedStates: LoomWingControlSurfacePose["states"];
  readonly fixedBodyCount: number;
  readonly fixedBodyBufferSignature: string;
  readonly fixedBodyObjectSignature: string;
  readonly fixedWingBuildCount: number;
  readonly topologyBuildCount: number;
  readonly poseApplyCount: number;
  readonly bodyCount: number;
  readonly hingeCount: number;
  readonly definitionRevision: LoomSha256Revision;
  readonly poseRevision: LoomSha256Revision;
  readonly fixedBodyTransformSignature: string;
  readonly visualizationAuthority: "server-evaluated-ephemeral-complementary-bodies";
  readonly visualTreatment: "complementary-fixed-and-movable-bodies";
}

interface BodyRuntime {
  body: THREE.Group;
  material: THREE.MeshPhysicalMaterial;
  mesh: THREE.Mesh<THREE.BufferGeometry, THREE.MeshPhysicalMaterial>;
  controlSurfaceId: string;
  side: "port" | "starboard";
  hingeStart: THREE.Vector3;
  hingeEnd: THREE.Vector3;
}

export interface LoomControlBodyWorldAnchor {
  readonly bodyId: string;
  readonly point: LoomPoint3;
}
export interface LoomControlBodyWorldPickCandidates {
  readonly bodyId: string;
  readonly points: readonly LoomPoint3[];
}

const objectIdentity = new WeakMap<object, number>();
let nextIdentity = 1;
function identity(value: object): string {
  let id = objectIdentity.get(value);
  if (!id) { id = nextIdentity++; objectIdentity.set(value, id); }
  return String(id);
}

function acceptedTopologyIdentity(geometry: LoomControlSurfaceAcceptedGeometry): string {
  return JSON.stringify([
    geometry.topologyRevision,
    ...geometry.bodies.map((body) => [
      body.bodyId, body.role, body.controlSurfaceId, body.side, body.geometryRevision,
      body.componentCount, body.positions.length, body.indices.length,
    ]),
  ]);
}

function disposeObject(object: THREE.Object3D): void {
  object.traverse((child) => {
    const mesh = child as THREE.Mesh<THREE.BufferGeometry, THREE.Material | THREE.Material[]>;
    mesh.geometry?.dispose();
    if (mesh.material) (Array.isArray(mesh.material) ? mesh.material : [mesh.material]).forEach((item) => item.dispose());
  });
}

function meshGeometry(positions: Float32Array, indices: Uint32Array): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions.slice(), 3));
  geometry.setIndex(new THREE.BufferAttribute(indices.slice(), 1));
  geometry.computeVertexNormals();
  return geometry;
}

function addMesh(
  parent: THREE.Group,
  bodyId: string,
  positions: Float32Array,
  indices: Uint32Array,
  color: string,
) {
  const geometry = meshGeometry(positions, indices);
  const fill = new THREE.MeshPhysicalMaterial({ clearcoat: 0.25, color, metalness: 0.03,
    roughness: 0.42, side: THREE.DoubleSide });
  const mesh = new THREE.Mesh(geometry, fill);
  mesh.name = `${bodyId}:surface`; mesh.userData.loomBodyId = bodyId; mesh.userData.loomPickable = true;
  const outline = new THREE.LineSegments(
    new THREE.EdgesGeometry(geometry, 24),
    new THREE.LineBasicMaterial({ color: "#5d2834", transparent: true, opacity: 0.72 }),
  );
  outline.name = `${bodyId}:outline`; parent.add(mesh, outline);
  return { material: fill, mesh };
}

function hingeLine(start: readonly number[], end: readonly number[], id: string): THREE.Line {
  const geometry = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(start[0], start[1], start[2]),
    new THREE.Vector3(end[0], end[1], end[2]),
  ]);
  const line = new THREE.Line(geometry, new THREE.LineBasicMaterial({ color: "#b44555", depthTest: false }));
  line.name = id; line.renderOrder = 24; line.userData.loomVisualRole = "control-surface-hinge-axis";
  return line;
}

export function markLoomFixedWingBody(scene: THREE.Group, wingId: string, selectedBodyId: string | null): number {
  const fixedId = `${wingId}:fixed`; let matched = false;
  scene.traverse((object) => {
    if (!(object as THREE.Mesh).isMesh || object.userData.loomFixedWingBodyId !== fixedId) return;
    matched = true; object.userData.loomSelected = selectedBodyId === fixedId;
  });
  return matched ? 1 : 0;
}

export class LoomControlSurfaceSceneController {
  private layer: THREE.Group | null = null;
  private topologyKey: string | null = null;
  private poseKey: string | null = null;
  private readonly bodies = new Map<string, BodyRuntime>();
  private readonly hiddenSourceObjects = new Map<THREE.Object3D, boolean>();
  private topologyBuildCount = 0;
  private poseApplyCount = 0;

  constructor(
    private readonly host: THREE.Group,
    private readonly fixedColor = "#527c90",
    private readonly controlColor = "#d28a36",
  ) {}

  private restoreSource(): void {
    this.hiddenSourceObjects.forEach((visible, object) => { object.visible = visible; });
    this.hiddenSourceObjects.clear();
  }

  private hideUncutWing(wingId: string): void {
    this.host.traverse((object) => {
      const inAcceptedLayer = object === this.layer || Boolean(this.layer?.getObjectById(object.id));
      const hidesWing = object.userData.loomPresentationEntityId === wingId
        || object.userData.loomEditableNodeId === wingId
        || object.userData.loomMirrorSourceNodeId === wingId;
      if (inAcceptedLayer || !hidesWing || !object.visible) return;
      this.hiddenSourceObjects.set(object, object.visible); object.visible = false;
    });
  }

  private rebuild(input: SyncInput): void {
    const layer = new THREE.Group(); layer.name = "Loom accepted complementary control geometry";
    layer.userData.loomVisualizationAuthority = "server-evaluated-ephemeral-complementary-bodies";
    layer.userData.loomTopologyRevision = input.geometry.topologyRevision;
    const bodies = new Map<string, BodyRuntime>();
    try {
      const fixed = input.geometry.bodies.find(({ role }) => role === "fixed-wing");
      if (!fixed) throw new Error("accepted control geometry omitted the fixed wing body");
      const fixedGroup = new THREE.Group(); fixedGroup.name = fixed.bodyId;
      const fixedMesh = addMesh(fixedGroup, fixed.bodyId, fixed.positions, fixed.indices, this.fixedColor);
      fixedMesh.mesh.userData.loomFixedWingBodyId = fixed.bodyId;
      fixedMesh.mesh.userData.loomEditableNodeId = input.collection.wingId;
      fixedGroup.userData.loomBodyId = fixed.bodyId; fixedGroup.userData.loomGeometryRevision = fixed.geometryRevision;
      layer.add(fixedGroup);
      input.geometry.bodies.filter(({ role }) => role === "movable-control").forEach((accepted) => {
        if (!accepted.controlSurfaceId || !accepted.side) throw new Error("accepted movable body ownership is incomplete");
        const hinge = input.geometry.hinges.find(({ controlSurfaceId, side }) => (
          controlSurfaceId === accepted.controlSurfaceId && side === accepted.side
        ));
        if (!hinge) throw new Error(`accepted movable body ${accepted.bodyId} omitted its hinge`);
        const body = new THREE.Group(); body.name = accepted.bodyId; body.matrixAutoUpdate = false;
        const built = addMesh(body, accepted.bodyId, accepted.positions, accepted.indices, this.controlColor);
        Object.assign(built.mesh.userData, { loomControlBodyId: accepted.bodyId,
          loomControlSurfaceId: accepted.controlSurfaceId, loomControlSide: accepted.side });
        body.userData = { loomBodyId: accepted.bodyId, loomControlSurfaceId: accepted.controlSurfaceId,
          loomControlSide: accepted.side, loomGeometryRevision: accepted.geometryRevision,
          loomVisualizationAuthority: "server-evaluated-ephemeral-complementary-bodies" };
        layer.add(body, hingeLine(hinge.startM, hinge.endM, hinge.hingeId));
        bodies.set(accepted.bodyId, { body, material: built.material, mesh: built.mesh,
          side: accepted.side, controlSurfaceId: accepted.controlSurfaceId,
          hingeStart: new THREE.Vector3(...hinge.startM), hingeEnd: new THREE.Vector3(...hinge.endM) });
      });
      this.applyPoseTo(bodies, input);
    } catch (reason) {
      disposeObject(layer);
      throw reason;
    }
    const previous = this.layer;
    if (!previous) this.hideUncutWing(input.collection.wingId);
    this.host.add(layer); this.layer = layer;
    this.bodies.clear(); bodies.forEach((body, id) => this.bodies.set(id, body));
    this.topologyKey = acceptedTopologyIdentity(input.geometry); this.poseKey = input.poseRevision;
    this.topologyBuildCount += 1; this.poseApplyCount += 1;
    if (previous) { previous.removeFromParent(); disposeObject(previous); }
  }

  private applyPoseTo(bodies: ReadonlyMap<string, BodyRuntime>, input: SyncInput): void {
    const states = new Map(input.pose.states.map((state) => [state.controlSurfaceId, state]));
    bodies.forEach((runtime) => {
      const state = states.get(runtime.controlSurfaceId);
      if (!state) throw new Error(`accepted body ${runtime.controlSurfaceId} has no pose state`);
      const semantic = runtime.side === "port" ? state.portDeflectionDeg : state.starboardDeflectionDeg;
      const physical = THREE.MathUtils.degToRad(semantic) * (runtime.side === "port" ? -1 : 1);
      const axis = runtime.hingeEnd.clone().sub(runtime.hingeStart).normalize();
      const rotation = new THREE.Matrix4().makeRotationAxis(axis, physical);
      runtime.body.matrix.copy(new THREE.Matrix4().makeTranslation(...runtime.hingeStart.toArray())
        .multiply(rotation).multiply(new THREE.Matrix4().makeTranslation(...runtime.hingeStart.clone().negate().toArray())));
      runtime.body.userData.loomSemanticTeDownDeg = semantic;
      runtime.body.userData.loomTransformSignature = `rigid:${semantic.toFixed(6)}`;
    });
  }

  private applyPose(input: SyncInput): void {
    this.applyPoseTo(this.bodies, input);
    this.poseKey = input.poseRevision; this.poseApplyCount += 1;
  }

  sync(input: SyncInput): LoomControlSurfaceSceneReceipt {
    if (!this.layer?.parent || this.topologyKey !== acceptedTopologyIdentity(input.geometry)) this.rebuild(input);
    if (this.poseKey !== input.poseRevision) this.applyPose(input);
    this.bodies.forEach((runtime, id) => {
      const selected = id === input.selectedBodyId;
      runtime.material.emissive.set(this.controlColor);
      runtime.material.emissiveIntensity = selected ? 0.26 : 0.04;
      runtime.body.userData.loomSelected = selected;
    });
    this.host.updateMatrixWorld(true);
    const appliedStates = input.pose.states.map((state) => ({ ...state }));
    const appliedAnglesDeg = Object.fromEntries(appliedStates.map((state) => [state.controlSurfaceId, {
      port: state.portDeflectionDeg, starboard: state.starboardDeflectionDeg,
    }]));
    const fixed = input.geometry.bodies.find(({ role }) => role === "fixed-wing")!;
    return { appliedAnglesDeg, appliedStates, fixedBodyCount: 1,
      fixedBodyBufferSignature: `core:${fixed.geometryRevision}:${fixed.positions.length}:${fixed.indices.length}`,
      fixedBodyObjectSignature: `core-object:${identity(this.layer!)}`,
      fixedWingBuildCount: this.topologyBuildCount, topologyBuildCount: this.topologyBuildCount,
      poseApplyCount: this.poseApplyCount, bodyCount: this.bodies.size,
      hingeCount: input.geometry.hinges.length, definitionRevision: input.geometry.definitionRevision,
      poseRevision: input.poseRevision, fixedBodyTransformSignature: "identity:server-fixed-wing",
      visualizationAuthority: "server-evaluated-ephemeral-complementary-bodies",
      visualTreatment: "complementary-fixed-and-movable-bodies" };
  }

  hasAcceptedLayer(): boolean {
    return Boolean(this.layer?.parent);
  }

  controlBodyWorldAnchors(): readonly LoomControlBodyWorldAnchor[] {
    this.host.updateMatrixWorld(true);
    return [...this.bodies].map(([bodyId, runtime]) => {
      const center = new THREE.Box3().setFromObject(runtime.body).getCenter(new THREE.Vector3());
      return { bodyId, point: [center.x, center.y, center.z] as const };
    });
  }

  controlBodyWorldPickCandidates(): readonly LoomControlBodyWorldPickCandidates[] {
    this.host.updateMatrixWorld(true);
    return [...this.bodies].map(([bodyId, { mesh }]) => {
      const positions = mesh.geometry.getAttribute("position"); const index = mesh.geometry.getIndex();
      const triangleCount = (index?.count ?? positions.count) / 3;
      const selected = new Set<number>([0, Math.floor(triangleCount / 2), triangleCount - 1]);
      for (let sample = 1; sample < 7; sample += 1) selected.add(Math.floor(triangleCount * sample / 7));
      const points = [...selected].filter((triangle) => triangle >= 0 && triangle < triangleCount).map((triangle): LoomPoint3 => {
        const point = new THREE.Vector3();
        for (let corner = 0; corner < 3; corner += 1) {
          const vertex = index?.getX(triangle * 3 + corner) ?? triangle * 3 + corner;
          point.add(new THREE.Vector3().fromBufferAttribute(positions, vertex));
        }
        point.multiplyScalar(1 / 3).applyMatrix4(mesh.matrixWorld);
        return [point.x, point.y, point.z];
      });
      return { bodyId, points };
    });
  }

  dispose(): void {
    if (this.layer) { this.layer.removeFromParent(); disposeObject(this.layer); }
    this.restoreSource(); this.layer = null; this.bodies.clear(); this.topologyKey = null; this.poseKey = null;
  }
}

export function resolveLoomControlBodyAtCanvasPoint(
  scene: THREE.Group, camera: THREE.PerspectiveCamera,
  x: number, y: number, width: number, height: number,
): string | null {
  if (width <= 0 || height <= 0) return null;
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(new THREE.Vector2((x / width) * 2 - 1, 1 - (y / height) * 2), camera);
  const hits = raycaster.intersectObjects(scene.children, true).filter(({ object }) => (
    (typeof object.userData.loomControlBodyId === "string" || typeof object.userData.loomFixedWingBodyId === "string")
    && object.visible
  ));
  const hit = hits.find(({ object }) => typeof object.userData.loomControlBodyId === "string") ?? hits[0];
  const bodyId = hit?.object.userData.loomControlBodyId ?? hit?.object.userData.loomFixedWingBodyId;
  return typeof bodyId === "string" ? bodyId : null;
}

export function distanceFromPointToAxis(point: THREE.Vector3, start: THREE.Vector3, end: THREE.Vector3): number {
  const axis = end.clone().sub(start); const lengthSquared = axis.lengthSq();
  const t = point.clone().sub(start).dot(axis) / lengthSquared;
  return point.distanceTo(start.clone().add(axis.multiplyScalar(t)));
}
